import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { useTheme as useGlobalTheme } from "../../store/contexts/ThemeContext";
import { ROUTES } from "../../routes/User/paths";
import PublicHeader from "../../components/PublicHeader/PublicHeader";
import PublicFooter from "../../components/PublicFooter/PublicFooter";
import ScrollToTop from "../../components/ScrollToTop/ScrollToTop";
import {
  Box,
  Button,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  useTheme,
  Chip,
  Fade,
  Zoom,
  IconButton,
  useMediaQuery,
} from "@mui/material";
import {
  VideoCall,
  People,
  Security,
  ArrowForward,
  EmojiEvents,
  Verified,
  PlayArrow,
  Close,
  TrendingUp,
  AutoAwesome,
  CheckCircle,
  Groups,
  ScreenshotMonitor,
  Campaign,
  RocketLaunch,
} from "@mui/icons-material";
import "./Landing.css";

const Landing = () => {
  const history = useHistory();
  const theme = useTheme();
  const { darkMode, toggleTheme } = useGlobalTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isTablet = useMediaQuery(theme.breakpoints.down("lg"));
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeFeature, setActiveFeature] = useState(0);

  const appFeatures = [
    {
      icon: <VideoCall sx={{ fontSize: 48 }} />,
      title: "Live Video Rooms",
      description: "Host HD video rooms with up to 50 participants. Screen sharing, virtual backgrounds, and crystal-clear audio.",
      gradient: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
      stats: "50+ Participants",
    },
    {
      icon: <Groups sx={{ fontSize: 48 }} />,
      title: "Team Collaboration",
      description: "Create teams within your room for gaming tournaments, study groups, or project collaboration with dedicated chat.",
      gradient: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
      stats: "Up to 5 Teams",
    },
    {
      icon: <ScreenshotMonitor sx={{ fontSize: 48 }} />,
      title: "Monetized Screenshots",
      description: "Earn from every screenshot your viewers take. Set custom pricing and watch your passive income grow automatically.",
      gradient: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
      stats: "Custom Pricing",
    },
    {
      icon: <EmojiEvents sx={{ fontSize: 48 }} />,
      title: "Tips & Rewards",
      description: "Receive instant tips from your audience. Built-in tipping system with real-time notifications and leaderboards.",
      gradient: "linear-gradient(135deg, #fa709a 0%, #fee140 100%)",
      stats: "Instant Payouts",
    },
    {
      icon: <Campaign sx={{ fontSize: 48 }} />,
      title: "Ad Revenue",
      description: "Monetize your content with strategic ad placements. Revenue sharing model with premium ad-free tiers.",
      gradient: "linear-gradient(135deg, #30cfd0 0%, #330867 100%)",
      stats: "Revenue Share",
    },
    {
      icon: <Security sx={{ fontSize: 48 }} />,
      title: "Advanced Moderation",
      description: "Powerful moderation tools including user bans, auto-moderation rules, kick-out voting, and comprehensive reporting.",
      gradient: "linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)",
      stats: "Full Control",
    },
  ];

  const stats = [
    { value: "10K+", label: "Active Users", icon: <People /> },
    { value: "50K+", label: "Rooms Created", icon: <VideoCall /> },
    { value: "$100K+", label: "Earnings Paid", icon: <TrendingUp /> },
    { value: "99.9%", label: "Uptime", icon: <Verified /> },
  ];

  const memberships = [
    {
      tier: "Basic",
      price: "Free",
      description: "Perfect for getting started",
      features: [
        "1 Active Room",
        "12 Participants",
        "3 Teams per Room",
        "Basic Features",
        "Standard Support",
        "Ad-supported",
      ],
      icon: "🎯",
      recommended: false,
    },
    {
      tier: "Standard",
      price: "$1.99",
      period: "/month",
      description: "Most popular for creators",
      features: [
        "2 Active Rooms",
        "20 Participants",
        "5 Teams per Room",
        "Higher Earning Rates",
        "Priority Support",
        "Reduced Ads",
        "Analytics Dashboard",
      ],
      icon: "⚡",
      recommended: true,
    },
    {
      tier: "Premium",
      price: "$3.99",
      period: "/month",
      description: "For professional hosts",
      features: [
        "Unlimited Rooms",
        "50 Participants",
        "Unlimited Teams",
        "Highest Earning Rates",
        "No Ads",
        "VIP Badge",
        "24/7 Support",
        "Custom Branding",
      ],
      icon: "👑",
      recommended: false,
    },
  ];

  const howItWorks = [
    {
      step: 1,
      title: "Create Your Account",
      description: "Sign up in seconds with email or social login. Choose your membership tier and customize your profile.",
      icon: <RocketLaunch sx={{ fontSize: 40 }} />,
    },
    {
      step: 2,
      title: "Set Up Your Room",
      description: "Create your first room, set pricing, configure teams, and customize your space with your branding.",
      icon: <VideoCall sx={{ fontSize: 40 }} />,
    },
    {
      step: 3,
      title: "Go Live & Earn",
      description: "Start your room, invite participants, engage your audience, and earn from tips, screenshots, and ads.",
      icon: <AutoAwesome sx={{ fontSize: 40 }} />,
    },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveFeature((prev) => (prev + 1) % appFeatures.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [appFeatures.length]);

  return (
    <Box className={`landing-wrapper ${darkMode ? "dark" : "light"}`}>
      {/* Animated Gradient Mesh Background */}
      <Box className="gradient-mesh">
        <Box className="gradient-blob blob-1" />
        <Box className="gradient-blob blob-2" />
        <Box className="gradient-blob blob-3" />
        <Box className="gradient-blob blob-4" />
      </Box>

      {/* Navigation */}
      <PublicHeader scrolled={scrolled} />

      {/* Hero Section */}
      <Box className="hero-section">
        <Container maxWidth="lg">
          <Fade in timeout={800}>
            <Box className="hero-content">
              <Chip
                icon={<AutoAwesome />}
                label="Powered by Innovation"
                className="hero-chip"
              />
              <Typography variant="h1" className="hero-title">
                Connect, Create,
                <br />
                <span className="gradient-text">Earn Together</span>
              </Typography>
                <br />

              <Typography variant="h5" className="hero-subtitle">
                The ultimate live video platform where creators build communities,
                <br />
                engage audiences, and monetize their passion in real-time.
              </Typography>
                <br />

              <Box className="hero-cta">
                <Button
                  variant="contained"
                  size="large"
                  className="cta-primary"
                  endIcon={<ArrowForward />}
                  onClick={() => history.push(ROUTES.register)}
                >
                  Start Free Today
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  className="cta-secondary"
                  startIcon={<PlayArrow />}
                  onClick={() => setShowVideoModal(true)}
                >
                  Watch Demo
                </Button>
                 <Button
                  variant="contained"
                  size="large"
                  className="cta-primary"
                  endIcon={<ArrowForward />}
                  onClick={() => history.push(ROUTES.dashboard)}
                >
                  Go to Dashboard
                </Button>
                
              </Box>
              <Box className="trust-badges">
                <Chip icon={<Security />} label="Bank-level Security" size="small" />
                <Chip icon={<Verified />} label="Trusted by 10K+ Users" size="small" />
                <Chip label="💳 Stripe Powered" size="small" />
              </Box>
            </Box>
          </Fade>
        </Container>
      </Box>

      {/* Stats Section */}
      <Box className="stats-section">
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            {stats.map((stat, index) => (
              <Grid item xs={6} md={3} key={index}>
                <Zoom in timeout={500 + index * 100}>
                  <Box className="stat-card">
                    <Box className="stat-icon">{stat.icon}</Box>
                    <Typography variant="h3" className="stat-value">
                      {stat.value}
                    </Typography>
                    <Typography variant="body2" className="stat-label">
                      {stat.label}
                    </Typography>
                  </Box>
                </Zoom>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Features Section */}
      <Box id="features" className="features-section">
        <Container maxWidth="lg">
          <Box className="section-header">
            <Chip label="Platform Features" className="section-chip" />
            <Typography variant="h2" className="section-title">
              Everything You Need to <span className="gradient-text">Succeed</span>
            </Typography>
            <Typography variant="h6" className="gradient-text">
              Powerful tools designed for creators, streamers, and community builders
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {appFeatures.map((feature, index) => (
              <Grid item xs={12} sm={6} lg={4} key={index}>
                <Zoom in timeout={300 + index * 100}>
                  <Card className="feature-card" elevation={0}>
                    <Box
                      className="feature-icon-wrapper"
                      sx={{ background: feature.gradient }}
                    >
                      {feature.icon}
                    </Box>
                    <CardContent className="feature-content">
                      <Typography variant="h5" className="feature-title">
                        {feature.title}
                      </Typography>
                      <Typography variant="body2" className="feature-description">
                        {feature.description}
                      </Typography>
                      <Chip
                        label={feature.stats}
                        size="small"
                        className="feature-stat"
                        icon={<CheckCircle sx={{ fontSize: 16 }} />}
                      />
                    </CardContent>
                  </Card>
                </Zoom>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* How It Works */}
      <Box className="how-it-works-section">
        <Container maxWidth="lg">
          <Box className="section-header">
            <Chip label="Getting Started" className="section-chip" />
            <Typography variant="h2" className="section-title">
              Start Earning in <span className="gradient-text">3 Simple Steps</span>
            </Typography>
          </Box>

          <Grid container spacing={4} alignItems="center">
            {howItWorks.map((step, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Box className="step-card">
                  <Box className="step-number">{step.step}</Box>
                  <Box className="step-icon">{step.icon}</Box>
                  <Typography variant="h5" className="step-title">
                    {step.title}
                  </Typography>
                  <Typography variant="body1" className="step-description">
                    {step.description}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Pricing Section */}
      <Box id="pricing" className="pricing-section">
        <Container maxWidth="lg">
          <Box className="section-header">
            <Chip label="Flexible Pricing" className="section-chip" />
            <Typography variant="h2" className="section-title">
              Choose Your <span className="gradient-text">Perfect Plan</span>
            </Typography>
            <Typography variant="h6" className="gradient-text">
              Start free, upgrade as you grow. All plans include our core features.
            </Typography>
          </Box>

          <Grid container spacing={4} justifyContent="center">
            {memberships.map((plan, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Zoom in timeout={400 + index * 100}>
                  <Card className={`pricing-card ${plan.recommended ? "recommended" : ""}`} elevation={plan.recommended ? 8 : 0}>
                    {plan.recommended && (
                      <Box className="recommended-badge">
                        <Chip label="Most Popular" size="small" icon={<EmojiEvents />} />
                      </Box>
                    )}
                    <CardContent className="pricing-content">
                      <Box className="pricing-header">
                        <Typography className="tier-icon">{plan.icon}</Typography>
                        <Typography variant="h4" className="tier-name">
                          {plan.tier}
                        </Typography>
                        <Typography variant="body2" className="tier-description">
                          {plan.description}
                        </Typography>
                      </Box>

                      <Box className="pricing-price">
                        <Typography variant="h2" className="price">
                          {plan.price}
                        </Typography>
                        {plan.period && (
                          <Typography variant="body2" className="period">
                            {plan.period}
                          </Typography>
                        )}
                      </Box>

                      <Box className="pricing-features">
                        {plan.features.map((feature, idx) => (
                          <Box key={idx} className="feature-item">
                            <CheckCircle className="check-icon" />
                            <Typography variant="body2">{feature}</Typography>
                          </Box>
                        ))}
                      </Box>

                      <Button
                        variant={plan.recommended ? "contained" : "outlined"}
                        fullWidth
                        size="large"
                        className={`pricing-cta ${plan.recommended ? "primary" : ""}`}
                        onClick={() => history.push(ROUTES.register)}
                      >
                        Sign Up
                      </Button>
                    </CardContent>
                  </Card>
                </Zoom>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box className="final-cta-section">
        <Container maxWidth="md">
          <Box className="final-cta-content">
            <Typography variant="h2" className="cta-title">
              Ready to Build Your Community?
            </Typography>
            <Typography variant="h6" className="cta-subtitle">
              Join thousands of creators already earning on IOTA. Start your journey today.
            </Typography>
            <Box className="cta-buttons">
              <Button
                variant="contained"
                size="large"
                className="cta-primary"
                endIcon={<ArrowForward />}
                onClick={() => history.push(ROUTES.register)}
              >
                Create Free Account
              </Button>
              <Button
                variant="text"
                size="large"
                className="cta-link"
                onClick={() => history.push(ROUTES.login)}
              >
                Already have an account? Sign in →
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Footer */}
      <PublicFooter />

      {/* Scroll to Top */}
      <ScrollToTop />

      {/* Video Modal */}
      {showVideoModal && (
        <Fade in={showVideoModal}>
          <Box className="video-modal" onClick={() => setShowVideoModal(false)}>
            <Box className="video-modal-content" onClick={(e) => e.stopPropagation()}>
              <IconButton className="close-modal-btn" onClick={() => setShowVideoModal(false)}>
                <Close />
              </IconButton>
              <Typography variant="h4" className="modal-title">
                See IOTA in Action
              </Typography>
              <Box className="video-placeholder">
                <PlayArrow sx={{ fontSize: 80 }} />
                <Typography variant="h6" className="placeholder-text">
                  Demo video coming soon
                </Typography>
                <Typography variant="body2" className="placeholder-subtext">
                  Experience live rooms, team collaboration, and monetization
                </Typography>
              </Box>
            </Box>
          </Box>
        </Fade>
      )}
    </Box>
  );
};

export default Landing;
